import { useCallback, useEffect, useMemo, useState } from "react";

import { createProduct, deleteProduct, fetchProducts, updateProduct, uploadAdminMedia } from "@/features/admin/api";
import { useAdminSession } from "@/features/admin/useAdminSession";
import type { AdminProduct } from "@/features/admin/types";
import PaginationControls from "./PaginationControls";

const defaultProductImage =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 400'><rect width='640' height='400' fill='%23F7F7F5'/><rect x='32' y='32' width='576' height='336' rx='32' fill='%23FFF3ED'/><circle cx='190' cy='144' r='44' fill='%23E8521A' fill-opacity='0.25'/><path d='M96 312l118-108 94 82 86-68 150 94' fill='none' stroke='%23E8521A' stroke-width='26' stroke-linecap='round' stroke-linejoin='round'/></svg>";

const defaultCategories = [
  "vetements",
  "accessoires",
  "sport",
  "papeterie"
];
const PRODUCTS_PER_PAGE = 5;

function normalizeCategoryKey(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function formatCategoryLabel(value: string) {
  return value
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function resolveCategoryIcon(value: string) {
  const normalized = normalizeCategoryKey(value);

  switch (normalized) {
    case "vetements":
      return "ri-t-shirt-line";
    case "accessoires":
      return "ri-shopping-bag-line";
    case "sport":
      return "ri-basketball-line";
    case "papeterie":
      return "ri-book-line";
    case "electromenager":
      return "ri-home-gear-line";
    default:
      return "ri-price-tag-3-line";
  }
}

type ProductFormState = {
  name: string;
  description: string;
  price: string;
  category: string;
  customCategory: string;
  stock: string;
  badge: string;
};

const emptyForm = (): ProductFormState => ({
  name: "",
  description: "",
  price: "",
  category: defaultCategories[0] ?? "vetements",
  customCategory: "",
  stock: "",
  badge: ""
});

export default function BoutiqueTab() {
  const { request } = useAdminSession();
  const [items, setItems] = useState<AdminProduct[]>([]);
  const [filterCat, setFilterCat] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<AdminProduct | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<ProductFormState>(() => emptyForm());
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [imageInputKey, setImageInputKey] = useState(0);
  const [stockEditor, setStockEditor] = useState<{ product: AdminProduct; value: string } | null>(null);
  const [stockSaving, setStockSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const nextProducts = await fetchProducts(request);
      setItems(nextProducts);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger les produits";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    void loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    if (!selectedImageFile) {
      return;
    }

    const preview = URL.createObjectURL(selectedImageFile);
    setImagePreviewUrl(preview);

    return () => URL.revokeObjectURL(preview);
  }, [selectedImageFile]);

  const categories = useMemo(() => {
    const seen = new Map<string, { id: string; name: string; icon: string }>();
    seen.set("all", { id: "all", name: "Tous", icon: "ri-apps-line" });

    defaultCategories.forEach((category) => {
      seen.set(category, {
        id: category,
        name: formatCategoryLabel(category),
        icon: resolveCategoryIcon(category)
      });
    });

    items.forEach((product) => {
      const normalized = normalizeCategoryKey(product.category);
      if (!normalized) {
        return;
      }

      if (!seen.has(normalized)) {
        seen.set(normalized, {
          id: normalized,
          name: formatCategoryLabel(product.category),
          icon: resolveCategoryIcon(product.category)
        });
      }
    });

    return Array.from(seen.values());
  }, [items]);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  };

  const openNew = () => {
    setEditItem(null);
    setForm(emptyForm());
    setSelectedImageFile(null);
    setImagePreviewUrl(null);
    setImageInputKey((current) => current + 1);
    setShowForm(true);
  };

  const openEdit = (product: AdminProduct) => {
    const normalizedCategory = normalizeCategoryKey(product.category);
    const isDefaultCategory = defaultCategories.includes(normalizedCategory);

    setEditItem(product);
    setForm({
      name: product.name,
      description: product.description,
      price: String(product.price),
      category: isDefaultCategory ? normalizedCategory : defaultCategories[0] ?? "vetements",
      customCategory: isDefaultCategory ? "" : product.category,
      stock: String(product.stock),
      badge: product.badge ?? ""
    });
    setSelectedImageFile(null);
    setImagePreviewUrl(product.image ?? null);
    setImageInputKey((current) => current + 1);
    setShowForm(true);
  };

  const handleSave = async () => {
    const resolvedCategory = form.customCategory.trim() || form.category;

    if (!form.name.trim()) {
      setError("Merci de renseigner le nom du produit.");
      return;
    }

    if (form.description.trim().length < 10) {
      setError("Merci de saisir une description d'au moins 10 caractères.");
      return;
    }

    if (!form.price || Number(form.price) <= 0) {
      setError("Merci de saisir un prix valide supérieur à 0.");
      return;
    }

    if (!resolvedCategory.trim()) {
      setError("Merci de choisir ou saisir une catégorie.");
      return;
    }

    if (Number(form.stock) < 0) {
      setError("Le stock ne peut pas être négatif.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let image = editItem?.image ?? null;
      let imagePublicId = editItem?.imagePublicId ?? null;
      let imageResourceType = editItem?.imageResourceType ?? null;

      if (selectedImageFile) {
        const [asset] = await uploadAdminMedia(request, [selectedImageFile], "asc-sonatel/boutique");
        image = asset?.url ?? image;
        imagePublicId = asset?.publicId ?? imagePublicId;
        imageResourceType = asset?.resourceType ?? imageResourceType;
      }

      const payload: Partial<AdminProduct> = {
        name: form.name.trim(),
        description: form.description.trim(),
        price: Number(form.price),
        category: normalizeCategoryKey(resolvedCategory),
        stock: Number(form.stock) || 0,
        badge: form.badge.trim() || null,
        image,
        imagePublicId,
        imageResourceType,
        isActive: (Number(form.stock) || 0) > 0
      };

      if (editItem) {
        await updateProduct(request, editItem.id, payload);
        showToast("Produit mis à jour");
      } else {
        await createProduct(request, {
          name: payload.name!,
          description: payload.description!,
          price: payload.price!,
          category: payload.category!,
          stock: payload.stock!,
          badge: payload.badge ?? null,
          image: payload.image ?? null,
          imagePublicId: payload.imagePublicId ?? null,
          imageResourceType: payload.imageResourceType ?? null,
          isActive: payload.isActive ?? true
        });
        showToast("Produit ajouté");
      }

      setShowForm(false);
      setSelectedImageFile(null);
      setImagePreviewUrl(null);
      setImageInputKey((current) => current + 1);
      await loadProducts();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Enregistrement impossible";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteProduct(request, id);
      showToast("Produit supprimé");
      await loadProducts();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Suppression impossible";
      setError(message);
    }
  };

  const handleToggleStock = async (product: AdminProduct) => {
    const nextStock = product.stock > 0 ? 0 : 10;

    try {
      await updateProduct(request, product.id, {
        stock: nextStock,
        isActive: nextStock > 0
      });
      showToast(nextStock > 0 ? "Produit remis en stock" : "Produit marqué en rupture");
      await loadProducts();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Action impossible";
      setError(message);
    }
  };

  const handleSaveStock = async () => {
    if (!stockEditor) {
      return;
    }

    const nextStock = Number(stockEditor.value);

    if (!Number.isFinite(nextStock) || nextStock < 0) {
      setError("Merci de saisir un stock valide supérieur ou égal à 0.");
      return;
    }

    setStockSaving(true);
    setError(null);

    try {
      await updateProduct(request, stockEditor.product.id, {
        stock: nextStock,
        isActive: nextStock > 0
      });
      showToast("Stock mis à jour");
      setStockEditor(null);
      await loadProducts();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Mise à jour du stock impossible";
      setError(message);
    } finally {
      setStockSaving(false);
    }
  };

  const filtered = useMemo(() => {
    return items.filter((product) => filterCat === "all" || normalizeCategoryKey(product.category) === filterCat);
  }, [filterCat, items]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PRODUCTS_PER_PAGE));
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return filtered.slice(startIndex, startIndex + PRODUCTS_PER_PAGE);
  }, [currentPage, filtered]);

  const totalValue = useMemo(() => {
    return items.reduce((accumulator, product) => accumulator + product.price * product.stock, 0);
  }, [items]);

  const outOfStock = useMemo(() => {
    return items.filter((product) => product.stock === 0 || !product.isActive).length;
  }, [items]);

  const lowStock = useMemo(() => {
    return items.filter((product) => product.stock > 0 && product.stock <= 5).length;
  }, [items]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterCat]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  return (
    <div className="relative space-y-6">
      {toast ? (
        <div className="fixed right-6 top-6 z-50 flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-3 text-sm font-medium text-white shadow-lg">
          <i className="ri-checkbox-circle-line text-lg"></i>
          {toast}
        </div>
      ) : null}

      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-xl font-bold text-[#1A1A1A] md:text-2xl">Gestion de la Boutique</h1>
          <p className="mt-1 text-sm text-gray-500">Gérez les produits, leurs photos, les stocks et les catégories libres de la boutique.</p>
          {error ? <p className="mt-2 text-sm text-red-500">{error}</p> : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => void loadProducts()}
            className="flex items-center gap-2 whitespace-nowrap rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:border-[#E8521A] hover:text-[#E8521A]"
          >
            <i className="ri-refresh-line"></i>
            Actualiser
          </button>
          <button
            onClick={openNew}
            className="flex items-center gap-2 whitespace-nowrap rounded-xl bg-[#E8521A] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#d44a16]"
          >
            <i className="ri-add-line"></i>
            Ajouter un produit
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <div className="rounded-2xl border border-gray-100 bg-white p-4">
          <div className="text-2xl font-bold text-[#1A1A1A]">{items.length}</div>
          <div className="mt-1 text-sm text-gray-500">Produits total</div>
        </div>
        <div className="rounded-2xl border border-gray-100 bg-white p-4">
          <div className="text-2xl font-bold text-red-500">{outOfStock}</div>
          <div className="mt-1 text-sm text-gray-500">Rupture de stock</div>
        </div>
        <div className="rounded-2xl border border-gray-100 bg-white p-4">
          <div className="text-2xl font-bold text-amber-500">{lowStock}</div>
          <div className="mt-1 text-sm text-gray-500">Stock faible</div>
        </div>
        <div className="rounded-2xl border border-gray-100 bg-white p-4">
          <div className="text-2xl font-bold text-[#1A8C7A]">{(totalValue / 1000).toFixed(0)}k FCFA</div>
          <div className="mt-1 text-sm text-gray-500">Valeur du stock</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setFilterCat(category.id)}
            className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-all whitespace-nowrap ${
              filterCat === category.id
                ? "bg-[#E8521A] text-white"
                : "border border-gray-200 bg-white text-gray-600 hover:border-[#E8521A] hover:text-[#E8521A]"
            }`}
          >
            <i className={category.icon}></i>
            {category.name}
            <span className={`rounded-full px-1.5 py-0.5 text-xs ${filterCat === category.id ? "bg-white/20" : "bg-gray-100"}`}>
              {category.id === "all" ? items.length : items.filter((product) => normalizeCategoryKey(product.category) === category.id).length}
            </span>
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-[760px] w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Produit</th>
                <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Catégorie</th>
                <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Prix</th>
                <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Stock</th>
                <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Badge</th>
                <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <tr key={index}>
                    <td colSpan={6} className="px-5 py-4">
                      <div className="h-14 animate-pulse rounded-xl bg-gray-100"></div>
                    </td>
                  </tr>
                ))
              ) : (
                paginatedProducts.map((product) => (
                  <tr key={product.id} className="transition-colors hover:bg-gray-50">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={product.image ?? defaultProductImage}
                          alt={product.name}
                          className="h-12 w-12 flex-shrink-0 rounded-xl object-cover object-top"
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-semibold text-[#1A1A1A]">{product.name}</div>
                            {!product.isActive ? (
                              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] uppercase tracking-wide text-gray-500">Masqué</span>
                            ) : null}
                          </div>
                          <div className="max-w-[260px] line-clamp-1 text-xs text-gray-500">{product.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs capitalize text-gray-600">
                        {formatCategoryLabel(product.category)}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm font-semibold text-[#1A1A1A]">{product.price.toLocaleString()} FCFA</td>
                    <td className="px-5 py-4">
                      <span
                        className={`text-sm font-semibold ${
                          product.stock === 0 || !product.isActive
                            ? "text-red-500"
                            : product.stock <= 5
                              ? "text-amber-500"
                              : "text-emerald-600"
                        }`}
                      >
                        {product.stock}
                      </span>
                      {product.stock === 0 || !product.isActive ? (
                        <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-500">Rupture</span>
                      ) : null}
                      {product.stock > 0 && product.stock <= 5 ? (
                        <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-600">Faible</span>
                      ) : null}
                    </td>
                    <td className="px-5 py-4">
                      {product.badge ? (
                        <span className="rounded-full bg-orange-100 px-2.5 py-1 text-xs font-medium text-[#E8521A]">{product.badge}</span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => void handleToggleStock(product)}
                          className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
                            product.stock > 0 && product.isActive
                              ? "bg-amber-100 text-amber-600 hover:bg-amber-200"
                              : "bg-emerald-100 text-emerald-600 hover:bg-emerald-200"
                          }`}
                          title={product.stock > 0 && product.isActive ? "Marquer rupture" : "Remettre en stock"}
                        >
                          <i className={`${product.stock > 0 && product.isActive ? "ri-close-circle-line" : "ri-add-circle-line"} text-sm`}></i>
                        </button>
                        <button
                          onClick={() => setStockEditor({ product, value: String(product.stock) })}
                          className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-500 transition-colors hover:bg-blue-100"
                          title="Modifier le stock"
                        >
                          <i className="ri-stack-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => openEdit(product)}
                          className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-gray-600 transition-colors hover:bg-orange-100 hover:text-[#E8521A]"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => void handleDelete(product.id)}
                          className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-400 transition-colors hover:bg-red-100"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {!loading && filtered.length === 0 ? (
            <div className="py-12 text-center text-gray-400">
              <i className="ri-shopping-bag-3-line mb-3 block text-4xl"></i>
              <p className="text-sm">Aucun produit dans cette catégorie</p>
            </div>
          ) : null}
        </div>
      </div>

      {!loading && filtered.length > PRODUCTS_PER_PAGE ? (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemLabel="produits"
        />
      ) : null}

      {showForm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-3xl overflow-hidden rounded-[28px] border border-gray-100 bg-white shadow-[0_30px_100px_rgba(0,0,0,0.16)]" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
              <div>
                <h3 className="text-lg font-bold text-[#1A1A1A]">{editItem ? "Modifier le produit" : "Nouveau produit"}</h3>
                <p className="mt-1 text-sm text-gray-500">Ajoutez l’image, le stock et une catégorie libre si nécessaire.</p>
              </div>
              <button onClick={() => setShowForm(false)} className="flex h-9 w-9 items-center justify-center rounded-xl text-gray-500 transition-colors hover:bg-gray-100">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="max-h-[80vh] space-y-5 overflow-auto p-6">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Nom du produit *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                    placeholder="Nom du produit..."
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm transition-colors focus:border-[#E8521A] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Badge marketing</label>
                  <input
                    type="text"
                    value={form.badge}
                    onChange={(event) => setForm((current) => ({ ...current, badge: event.target.value }))}
                    placeholder="Nouveau, Populaire..."
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm transition-colors focus:border-[#E8521A] focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Description *</label>
                <textarea
                  value={form.description}
                  onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                  placeholder="Description du produit..."
                  rows={4}
                  maxLength={500}
                  className="w-full resize-none rounded-xl border border-gray-200 px-4 py-3 text-sm transition-colors focus:border-[#E8521A] focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Prix (FCFA) *</label>
                  <input
                    type="number"
                    value={form.price}
                    onChange={(event) => setForm((current) => ({ ...current, price: event.target.value }))}
                    placeholder="15000"
                    min="0"
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm transition-colors focus:border-[#E8521A] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Stock *</label>
                  <input
                    type="number"
                    value={form.stock}
                    onChange={(event) => setForm((current) => ({ ...current, stock: event.target.value }))}
                    placeholder="50"
                    min="0"
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm transition-colors focus:border-[#E8521A] focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Catégorie existante</label>
                  <select
                    value={form.category}
                    onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))}
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm transition-colors focus:border-[#E8521A] focus:outline-none"
                  >
                    {categories
                      .filter((category) => category.id !== "all")
                      .map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Nouvelle catégorie (optionnel)</label>
                  <input
                    type="text"
                    value={form.customCategory}
                    onChange={(event) => setForm((current) => ({ ...current, customCategory: event.target.value }))}
                    placeholder="Ex: électroménager"
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm transition-colors focus:border-[#E8521A] focus:outline-none"
                  />
                  <p className="mt-2 text-xs text-gray-500">Si ce champ est rempli, il remplace la catégorie choisie ci-dessus.</p>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-100 p-5">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Image du produit</label>
                  <input
                    key={imageInputKey}
                    type="file"
                    accept="image/*"
                    onChange={(event) => setSelectedImageFile(event.target.files?.[0] ?? null)}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:rounded-xl file:border-0 file:bg-orange-50 file:px-4 file:py-2.5 file:font-medium file:text-[#E8521A] hover:file:bg-orange-100"
                  />
                  <p className="mt-2 text-xs text-gray-500">L’image sera téléversée dans le stockage cloud sécurisé de l’application.</p>
                </div>

                <div className="mt-4 flex items-center gap-4">
                  <img
                    src={imagePreviewUrl || editItem?.image || defaultProductImage}
                    alt="Aperçu"
                    className="h-24 w-24 rounded-2xl border border-orange-100 object-cover"
                  />
                  <div className="text-sm text-gray-500">
                    <p>Aperçu de la photo produit.</p>
                    <p className="mt-1">Si vous ne changez rien, l’image actuelle restera attachée au produit.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-6 py-5">
              <button
                onClick={() => setShowForm(false)}
                className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={() => void handleSave()}
                disabled={saving}
                className="rounded-xl bg-[#E8521A] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#d44a16] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Enregistrement..." : editItem ? "Mettre à jour" : "Ajouter"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {stockEditor ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setStockEditor(null)}>
          <div className="w-full max-w-md rounded-[28px] border border-gray-100 bg-white shadow-[0_30px_100px_rgba(0,0,0,0.16)]" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
              <div>
                <h3 className="text-lg font-bold text-[#1A1A1A]">Modifier le stock</h3>
                <p className="mt-1 text-sm text-gray-500">{stockEditor.product.name}</p>
              </div>
              <button onClick={() => setStockEditor(null)} className="flex h-9 w-9 items-center justify-center rounded-xl text-gray-500 transition-colors hover:bg-gray-100">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="space-y-4 p-6">
              <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                Stock actuel : <span className="font-semibold text-gray-900">{stockEditor.product.stock}</span>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Nouveau stock</label>
                <input
                  type="number"
                  min={0}
                  value={stockEditor.value}
                  onChange={(event) => setStockEditor((current) => current ? { ...current, value: event.target.value } : current)}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm transition-colors focus:border-[#E8521A] focus:outline-none"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-6 py-5">
              <button
                onClick={() => setStockEditor(null)}
                className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={() => void handleSaveStock()}
                disabled={stockSaving}
                className="rounded-xl bg-[#E8521A] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#d44a16] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {stockSaving ? "Mise à jour..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
